/**
 * 🧍 **The two decisions behind a name above a head** — T176, protocol 27.
 *
 * 🎯 **haugur, 2026-09-09:** *"i need to have players name above the head of
 * their carachter."*
 *
 * ## 🔴 Why this is not in `players.ts` or `hud.ts` beside its callers
 *
 * ⚠️ **Neither of those files can be imported by a test on this workstation.**
 * `players.ts` fetches a GLTF over the network and needs a renderer;
 * `hud.ts` needs a DOM and a three.js camera. **So a decision left in either
 * one is a decision nothing checks** — and this project has shipped six green,
 * broken features in a single day for exactly that reason.
 *
 * ✅ **This is the same move `presence.ts` made and says so in its own header:**
 * *if a decision can be arithmetic over plain values, put it where a test can
 * reach it.* What is left in the callers is the DOM and the projection, which
 * genuinely need a browser.
 *
 * ⚠️ **There is no third decision hiding here.** Where the name is drawn on
 * screen is `Hud.pinToScreen`, which street names have used since the
 * beginning — **not a second copy of it.**
 */

/**
 * 🧍 **How far above the feet a name floats, in metres.**
 *
 * 🔴 **Measured from the FEET, because that is what the wire carries.**
 * `players[].y` is the ground the body stands on — protocol 26 says so in its
 * own comment — so this is a whole body plus a little air.
 *
 * ⚠️ **`EYE_HEIGHT` is 1.7 m, so the head is about 1.8 m** and this leaves
 * roughly a head's clearance above it.
 *
 * 🔴 **This is the class of number this project has got wrong five times by
 * measuring something ADJACENT** — a bounding box, a roof, a lip, a proxy. It
 * is written against the one dimension the protocol actually defines, and a
 * body's bounding box is deliberately not consulted: the settler's cloak
 * decides where that ends, and a cloak is not a head.
 */
export const NAME_HEIGHT = 2.1;

/**
 * 🧍 **How far away a name is still written, in metres.**
 *
 * 🎯 **haugur, 2026-09-09** gave the number the way he gave the torch's 8 m and
 * the bench's 100 m: **30 m.** About a house-length and a half — names for the
 * people you are actually near.
 *
 * 🔴 **It is also what stands in for a depth test, and that is the load-bearing
 * half.** The plate is HTML drawn over the canvas, so it has no depth buffer to
 * test against and **a name would otherwise be legible straight through a
 * mountain** from the far side of a valley. ⚠️ **Raising this makes that worse
 * rather than showing more names**, which is the reason to leave it alone.
 */
export const NAMEPLATE_RANGE = 30;

/** Feet on the ground, in world metres. */
export interface Standing {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

/**
 * 🧍 **Where to write somebody's name, or `null` for somebody who has none.**
 *
 * 🔴 **A nameless player is LEFT OUT rather than given a placeholder.**
 * Development runs `ALLOW_ANONYMOUS_BUILD=true` with **zero rows in `users`**,
 * so `null` is the ordinary case there and every body on dev returns `null`
 * from this. ⚠️ **A head labelled *Anonymous* invents a person — and invents
 * the SAME person on every head on screen**, which is worse than a bare head
 * rather than a friendlier version of it.
 *
 * ⚠️ **An empty or blank name counts as no name.** A display name is not
 * supposed to be blank, but a plate containing one space is an invisible
 * rectangle that still hides the world behind it, and nothing on screen would
 * say why.
 */
export function nameplateAt(name: string | null | undefined, feet: Standing): Standing | null {
  if (name === null || name === undefined) return null;
  if (name.trim() === '') return null;
  return { x: feet.x, y: feet.y + NAME_HEIGHT, z: feet.z };
}

/**
 * 🧍 **Is this name close enough to be written?** — {@link NAMEPLATE_RANGE}.
 *
 * ⚠️ **Compared as SQUARES.** This runs once per person per frame, sixty times
 * a second, to answer a yes-or-no question, and a square root would be taken
 * only to be thrown away.
 *
 * ⚠️ **Straight-line distance, height included.** Somebody 30 m straight up a
 * cliff is 30 m away, and dropping the `y` term would write their name across
 * the sky.
 */
export function nameIsLegible(eye: Standing, plate: Standing): boolean {
  const dx = plate.x - eye.x;
  const dy = plate.y - eye.y;
  const dz = plate.z - eye.z;
  return dx * dx + dy * dy + dz * dz <= NAMEPLATE_RANGE * NAMEPLATE_RANGE;
}
